import * as React from 'react'

export interface CarouselButtonProps {
  disabled: boolean
  onClick?: () => void
}

export interface CarouselData {
  containerRef: React.MutableRefObject<HTMLDivElement | null>
  nextButtonProps: CarouselButtonProps
  prevButtonProps: CarouselButtonProps
}

export function useCarousel(): CarouselData {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const observer = React.useRef<IntersectionObserver | null>(null)
  const [visibleIndexes, setVisibleIndexes] = React.useState<number[]>([])

  React.useEffect(() => {
    if (!ref.current) {
      return
    }

    // Update visible indexes on intersection change
    function handleIntersect(entries: IntersectionObserverEntry[]) {
      entries.forEach(entry => {
        const children = [...(ref.current?.children ?? [])]
        const index = children.findIndex(child => child === entry.target)
        if (entry.isIntersecting) {
          setVisibleIndexes(prev => [...prev, index])
        } else {
          setVisibleIndexes(prev => prev.filter(i => i !== index))
        }
      })
    }

    observer.current = new IntersectionObserver(handleIntersect, {
      root: ref.current,
      threshold: 1,
    })

    const children = [...ref.current.children]
    children.forEach(child => observer.current?.observe(child))

    return () => {
      observer.current?.disconnect()
      observer.current = null
    }
  }, [])

  const scrollBy = React.useCallback((index: number) => {
    if (!ref.current) {
      return
    }

    const node = ref.current.children[index]
    const rect = node.getBoundingClientRect()
    ref.current?.scrollBy({
      left: rect.left - ref.current.getBoundingClientRect().left,
      behavior: 'smooth',
    })
  }, [])

  const onNextClick = React.useCallback(() => {
    if (!ref.current) {
      return
    }

    const visibleItems = visibleIndexes.sort((a, b) => a - b)
    const nextIndex = Math.min(
      // oxlint-disable-next-line unicorn/prefer-at
      visibleItems[visibleItems.length - 1] + 1,
      ref.current.children.length - 1,
    )
    if (nextIndex < ref.current.children.length) {
      scrollBy(nextIndex)
    }
  }, [visibleIndexes, scrollBy])

  const onPreviousClick = React.useCallback(() => {
    if (!ref.current) {
      return
    }

    const visibleItems = visibleIndexes.sort((a, b) => a - b)
    const prevIndex = Math.max(visibleItems[0] - visibleItems.length, 0)
    scrollBy(prevIndex)
  }, [visibleIndexes, scrollBy])

  const length = ref.current?.children.length ?? 0
  const visibleItems = visibleIndexes.toSorted((a, b) => a - b)

  const nextButtonProps = React.useMemo(
    () => ({
      disabled: visibleItems.includes(length - 1),
      onClick: onNextClick,
    }),
    [visibleItems, length, onNextClick],
  )

  const prevButtonProps = React.useMemo(
    () => ({
      disabled: visibleItems.includes(0),
      onClick: onPreviousClick,
    }),
    [visibleItems, onPreviousClick],
  )

  return {
    containerRef: ref,
    nextButtonProps,
    prevButtonProps,
  }
}
